/**
 * AccessContext — caches /access/me once per session and exposes a `can`
 * helper to drive UI gating (hide buttons, hide nav links, etc.).
 *
 * Backend rules (access_control.py) decide the actual permission via:
 *   1. Per-user override (access_overrides)
 *   2. Per-tenant role rule (access_rules)
 *   3. Super-admin platform default (platform_settings.access_defaults)
 *   4. Built-in DEFAULT_RULES seed
 *
 * Owner-like roles (firm_owner / managing_partner / super_admin) are always
 * granted `firm` scope on every resource by the backend — `is_owner_like`
 * is returned alongside the matrix so the UI can short-circuit.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AccessCtx = createContext(null);

export function AccessProvider({ children }) {
    const { user } = useAuth();
    const [data, setData] = useState(null); // { role, is_owner_like, scopes }

    const refresh = useCallback(async () => {
        if (!user || user === false) return;
        try {
            const r = await api.get("/access/me");
            setData(r.data);
        } catch {
            setData({ role: user.role, is_owner_like: false, scopes: {} });
        }
    }, [user]);

    useEffect(() => {
        if (!user || user === false) {
            setData(null);
            return;
        }
        refresh();
    }, [user, refresh]);

    /** Returns true if the user has any scope other than "none" on (resource, action). */
    const can = useCallback((resource, action = "view") => {
        if (!data) return false;
        if (data.is_owner_like) return true;
        const scope = data.scopes?.[resource]?.[action];
        return !!scope && scope !== "none";
    }, [data]);

    return (
        <AccessCtx.Provider value={{ access: data, can, refresh }}>
            {children}
        </AccessCtx.Provider>
    );
}

export const useAccess = () => useContext(AccessCtx) || { access: null, can: () => false, refresh: () => {} };
